import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { getSymbol } from '../utils/currency';
import {
  CalendarBlank, Clock, Users, ShoppingCart, Check, Lightning,
  Hourglass, CaretDown, Info, Tag, Prohibit, X
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  parking: 'Parking', hotels: 'Hotel', gyms: 'Gym', hospital: 'Hospital',
  home: 'Home Space', coworking: 'Coworking', meeting_rooms: 'Meeting Room',
  event_spaces: 'Event Space', studios: 'Studio', storage: 'Storage',
  shops: 'Shop', malls: 'Mall Kiosk', halls: 'Hall', housing_societies: 'Society Space',
  religious_places: 'Religious Space', salons: 'Salon', clubs: 'Club',
  restaurants: 'Restaurant', sports: 'Sports Turf', ev_charging: 'EV Charging', education: 'Education'
};

const SLOT_LABELS = {
  hourly: 'Per Hour', daily: 'Per Day', weekly: 'Per Week',
  monthly: 'Per Month', per_session: 'Per Session', per_event: 'Per Event'
};

const CategoryBookingForm = ({ listing, blockedDates = new Set(), selectedUnit = null, onClearUnit, selectedService = null, onClearService, selectedVariantIndex = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cs = getSymbol(listing?.currency || listing?.country);

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const [error, setError] = useState('');

  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState(listing.open_time || '00:00');
  const [endTime, setEndTime] = useState(listing.close_time || '23:59');
  const [guests, setGuests] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [vehicleType, setVehicleType] = useState('');
  const [notes, setNotes] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotCounts, setSlotCounts] = useState({});
  const [totalCapacity, setTotalCapacity] = useState(1);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [closures, setClosures] = useState([]);

  const isHourly = config?.default_slot === 'hourly' || listing.pricing_model === 'hourly';

  // '10:30' -> minutes; closure windows for the selected start date
  const t2m = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const closureWindow = (c) => {
    if (c.duration_type === 'full_day') return [0, 1440];
    if (c.duration_type === 'first_half') return [0, 720];
    if (c.duration_type === 'second_half') return [720, 1440];
    const e = t2m(c.end_time);
    return [t2m(c.start_time), e === 0 ? 1440 : e];
  };
  const dayClosures = useMemo(() => closures.filter(c => c.date === startDate), [closures, startDate]);
  const fullDayClosure = useMemo(() => closures.find(c => c.duration_type === 'full_day') || null, [closures]);

  // Set default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    setStartDate(dateStr);
    setEndDate(dateStr);
  }, []);

  // Fetch category booking config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/bookings/category-config/${listing.category}`);
        setConfig(res.data.config);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchConfig();
  }, [listing.category]);

  // Auto-set end date based on slot type
  useEffect(() => {
    if (!config || !startDate) return;
    const slot = config.default_slot;
    const start = new Date(startDate);
    if (slot === 'daily' || slot === 'per_session' || slot === 'per_event') {
      setEndDate(startDate);
    } else if (slot === 'weekly') {
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      setEndDate(end.toISOString().split('T')[0]);
    } else if (slot === 'monthly') {
      const end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      setEndDate(end.toISOString().split('T')[0]);
    }
  }, [config, startDate]);

  // Fetch price preview whenever inputs change
  useEffect(() => {
    if (!startDate || !endDate) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/api/bookings/price-preview`, {
          listing_id: listing.listing_id,
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString(),
          start_time: startTime,
          end_time: endTime,
          guests: guests,
          add_ons: selectedAddOns,
          selected_variant_index: selectedVariantIndex,
          selected_service_id: selectedService?.service_id || null,
        });
        setPriceInfo(res.data);
      } catch (e) { /* ignore preview errors */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [listing.listing_id, startDate, endDate, startTime, endTime, guests, selectedAddOns, selectedVariantIndex, selectedService?.service_id]);

  // Fetch booked slots & capacity whenever date changes (used by hourly grid and the capacity banner shown for ALL pricing models)
  useEffect(() => {
    if (!startDate) return;
    setSlotsLoading(true);
    axios.get(`${API_URL}/api/availability/${listing.listing_id}/slots?date=${startDate}`)
      .then(res => {
        setBookedSlots(res.data.booked_slots || []);
        setSlotCounts(res.data.slot_counts || {});
        setTotalCapacity(Math.max(1, parseInt(res.data.total_capacity || 1)));
      })
      .catch(() => { setBookedSlots([]); setSlotCounts({}); setTotalCapacity(1); })
      .finally(() => setSlotsLoading(false));
  }, [startDate, listing.listing_id]);

  // Fetch seller closures (maintenance / lunch / holiday …) for the selected date range
  useEffect(() => {
    if (!startDate) { setClosures([]); return; }
    const to = endDate && endDate >= startDate ? endDate : startDate;
    axios.get(`${API_URL}/api/listings/${listing.listing_id}/closures?from_date=${startDate}&to_date=${to}`)
      .then(res => setClosures(res.data.closures || []))
      .catch(() => setClosures([]));
  }, [startDate, endDate, listing.listing_id]);

  const toggleAddOn = (key) => {
    setSelectedAddOns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const needsGuests = config?.required_booking_fields?.includes('guests') ||
    ['hotels', 'home', 'event_spaces', 'halls', 'housing_societies', 'religious_places', 'clubs', 'restaurants', 'meeting_rooms'].includes(listing.category);
  const needsVehicle = config?.required_booking_fields?.includes('vehicle_type');
  const isApproval = config?.booking_mode === 'approval';

  // Generate all 24 hourly slots (1-24) for the slot grid
  const hourlySlots = useMemo(() => {
    if (!isHourly) return [];
    const slots = [];
    for (let h = 0; h < 24; h++) {
      const hour = String(h).padStart(2, '0');
      const nextHour = String((h + 1) % 24).padStart(2, '0');
      const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      const bookedCount = parseInt(slotCounts?.[h] ?? slotCounts?.[String(h)] ?? 0) || 0;
      const availableCount = Math.max(0, totalCapacity - bookedCount);
      // Multi-capacity listings: only "fully booked" when ALL units occupied
      const isFullyBooked = totalCapacity > 1 ? availableCount === 0 : bookedSlots.some(bs => {
        const bsStart = parseInt(bs.start_time.split(':')[0]);
        const bsEnd = parseInt(bs.end_time.split(':')[0]);
        return h >= bsStart && h < (bsEnd === 0 ? 24 : bsEnd);
      });
      const isPast = (() => {
        if (!startDate) return false;
        const today = new Date().toISOString().split('T')[0];
        if (startDate > today) return false;
        if (startDate === today && h <= new Date().getHours()) return true;
        return false;
      })();
      const closure = dayClosures.find(c => {
        const [cs, ce] = closureWindow(c);
        return h * 60 < ce && (h + 1) * 60 > cs;
      });
      slots.push({
        hour: h,
        timeValue: `${hour}:00`,
        endValue: `${nextHour}:00`,
        label,
        isBooked: isFullyBooked,
        isPast,
        isClosed: !!closure,
        closureReason: closure?.reason_label || null,
        isAvailable: !isFullyBooked && !isPast && !closure,
        availableCount,
        bookedCount,
      });
    }
    return slots;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHourly, bookedSlots, startDate, slotCounts, totalCapacity, dayClosures]);

  const isSlotInSelection = (hour) => {
    if (!startTime || !endTime) return false;
    const selStart = parseInt(startTime.split(':')[0]);
    const selEnd = parseInt(endTime.split(':')[0]);
    return hour >= selStart && hour < (selEnd === 0 ? 24 : selEnd);
  };

  const handleSlotClick = (slot) => {
    if (!slot.isAvailable) return;
    const currentStart = parseInt(startTime.split(':')[0]);
    const currentEnd = parseInt(endTime.split(':')[0]);
    const selEnd = currentEnd === 0 ? 24 : currentEnd;
    // If clicking a slot outside current range, start fresh
    if (slot.hour < currentStart || slot.hour >= selEnd) {
      setStartTime(slot.timeValue);
      setEndTime(slot.endValue);
    } else {
      // Extend selection to this slot
      if (slot.hour >= currentStart) {
        setEndTime(slot.endValue);
      }
    }
  };

  // Check if selected dates overlap with blocked dates
  const hasBlockedDates = useMemo(() => {
    if (!blockedDates || blockedDates.size === 0 || !startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const ds = d.toISOString().split('T')[0];
      if (blockedDates.has(ds)) return true;
    }
    return false;
  }, [blockedDates, startDate, endDate]);

  const handleSubmit = async () => {
    if (!user) {
      navigate('/buyer/login', { state: { from: `/listing/${listing.listing_id}` } });
      return;
    }
    if (hasBlockedDates) {
      setError('Selected dates include unavailable dates. Please choose different dates.');
      return;
    }
    const closureConflict = (() => {
      if (fullDayClosure) return fullDayClosure;
      const bs = t2m(startTime);
      const beRaw = t2m(endTime);
      const be = beRaw <= bs ? 1440 : beRaw;
      return dayClosures.find(c => {
        const [cs, ce] = closureWindow(c);
        return bs < ce && be > cs;
      }) || null;
    })();
    if (closureConflict) {
      setError(`Space is closed during the selected time — ${closureConflict.reason_label}. Please pick another slot.`);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        listing_id: listing.listing_id,
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        start_time: startTime,
        end_time: endTime,
        guests: guests,
        add_ons: selectedAddOns,
        notes: notes || null,
        vehicle_type: vehicleType || null,
        selected_unit_id: selectedUnit?.unit_id || null,
        selected_service_id: selectedService?.service_id || null,
        selected_variant_index: selectedVariantIndex,
      };
      const res = await axios.post(`${API_URL}/api/bookings`, payload);
      // After booking is created, take buyer to a payment-method picker
      // (seller's configured Direct Payment methods). Old behaviour: jump
      // straight to the bookings list.
      const booking = res.data;
      if (booking?.payment_status === 'paid_direct') {
        // Already settled (instant booking with no payment needed) — go to bookings
        navigate('/bookings', { state: { newBooking: booking } });
      } else {
        navigate(`/bookings/${booking.booking_id}/pay`, {
          state: { newBooking: booking, listing },
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-2xl p-6 border border-slate-100 animate-pulse h-64"></div>;
  }

  const addOns = config?.add_ons || [];

  // Day-level availability (number of bookings overlapping the selected start date)
  const dayBookedCount = bookedSlots.length;
  const dayAvailableCount = Math.max(0, totalCapacity - dayBookedCount);
  const dayRatio = totalCapacity > 0 ? dayAvailableCount / totalCapacity : 1;
  const dayTier = dayRatio === 0 ? 'full' : dayRatio < 0.2 ? 'low' : dayRatio < 0.5 ? 'med' : 'good';
  const TIER_STYLES = {
    good: {
      bg: 'bg-gradient-to-br from-emerald-50 via-white to-emerald-100',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      dot: 'bg-gradient-to-br from-emerald-300 to-emerald-600',
      ring: 'ring-emerald-200/60',
    },
    med: {
      bg: 'bg-gradient-to-br from-amber-50 via-white to-amber-100',
      border: 'border-amber-200',
      text: 'text-amber-700',
      dot: 'bg-gradient-to-br from-amber-300 to-amber-600',
      ring: 'ring-amber-200/60',
    },
    low: {
      bg: 'bg-gradient-to-br from-orange-50 via-white to-orange-100',
      border: 'border-orange-200',
      text: 'text-orange-700',
      dot: 'bg-gradient-to-br from-orange-300 to-orange-600',
      ring: 'ring-orange-200/60',
    },
    full: {
      bg: 'bg-gradient-to-br from-rose-50 via-white to-rose-100',
      border: 'border-rose-200',
      text: 'text-rose-700',
      dot: 'bg-gradient-to-br from-rose-300 to-rose-600',
      ring: 'ring-rose-200/60',
    },
  };

  // 3D-look classes per slot, varying by tier
  const slotTierClass = (slot, selected) => {
    const base = 'relative overflow-hidden ring-1 transition-all duration-150';
    if (slot.isClosed) return `${base} bg-[repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9_6px,#e2e8f0_6px,#e2e8f0_12px)] border-slate-300 ring-slate-200 text-slate-400 cursor-not-allowed shadow-none`;
    if (slot.isBooked) return `${base} bg-gradient-to-br from-rose-400 via-rose-500 to-rose-700 border-rose-600 ring-rose-300/40 text-white cursor-not-allowed shadow-[0_4px_0_-1px_rgba(159,18,57,0.45),0_6px_14px_-4px_rgba(244,63,94,0.5)]`;
    if (slot.isPast)   return `${base} bg-slate-100 border-slate-200 ring-slate-100 text-slate-300 cursor-not-allowed shadow-none`;
    if (selected)      return `${base} bg-gradient-to-br from-blue-300 via-blue-500 to-blue-700 border-blue-600 ring-blue-300/40 text-white cursor-pointer shadow-[0_4px_0_-1px_rgba(30,64,175,0.5),0_8px_18px_-4px_rgba(59,130,246,0.55)] hover:-translate-y-0.5 active:translate-y-0`;
    const ratio = totalCapacity <= 1 ? 1 : (slot.availableCount / totalCapacity);
    if (ratio >= 0.5) return `${base} bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300 ring-emerald-100 text-emerald-700 cursor-pointer shadow-[0_2px_0_-1px_rgba(4,120,87,0.18),0_4px_10px_-4px_rgba(16,185,129,0.35)] hover:from-emerald-100 hover:to-emerald-200 hover:-translate-y-0.5 active:translate-y-0`;
    if (ratio >= 0.2) return `${base} bg-gradient-to-br from-amber-50 to-amber-100 border-amber-300 ring-amber-100 text-amber-700 cursor-pointer shadow-[0_2px_0_-1px_rgba(146,64,14,0.18),0_4px_10px_-4px_rgba(245,158,11,0.35)] hover:from-amber-100 hover:to-amber-200 hover:-translate-y-0.5 active:translate-y-0`;
    return `${base} bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300 ring-orange-100 text-orange-700 cursor-pointer shadow-[0_2px_0_-1px_rgba(154,52,18,0.18),0_4px_10px_-4px_rgba(251,146,60,0.4)] hover:from-orange-100 hover:to-orange-200 hover:-translate-y-0.5 active:translate-y-0`;
  };
  const slotSubTextClass = (slot, selected) => {
    if (slot.isBooked) return 'text-white/90';
    if (slot.isPast)   return 'text-slate-300';
    if (selected)      return 'text-white/90';
    if (totalCapacity <= 1) return 'text-emerald-600';
    const ratio = slot.availableCount / totalCapacity;
    if (ratio >= 0.5) return 'text-emerald-600';
    if (ratio >= 0.2) return 'text-amber-600';
    return 'text-orange-600';
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg" data-testid="booking-form">
      {/* Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-2xl font-bold text-slate-900">{cs}{listing.base_price}</span>
          <span className="text-sm text-slate-500">/ {SLOT_LABELS[listing.pricing_model] || listing.pricing_model}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
            isApproval ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
          }`} data-testid="booking-mode-badge">
            {isApproval ? <Hourglass size={12} weight="bold" /> : <Lightning size={12} weight="bold" />}
            {isApproval ? 'Approval Required' : 'Instant Booking'}
          </span>
          {config && (
            <span className="text-xs text-slate-400">{config.service}</span>
          )}
        </div>
      </div>

      {/* Universal Capacity Banner — premium 3D card with sheen + soft shadow */}
      {totalCapacity > 1 && startDate && (
        <div className={`mx-5 mt-4 rounded-2xl border ${TIER_STYLES[dayTier].border} ${TIER_STYLES[dayTier].bg} px-4 py-3 flex items-center justify-between relative overflow-hidden shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08),inset_0_1px_0_0_rgba(255,255,255,0.7)]`} data-testid="capacity-banner">
          {/* Top sheen */}
          <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
          <div className="flex items-center gap-3 relative z-10">
            <span className={`relative w-7 h-7 rounded-full ${TIER_STYLES[dayTier].dot} ring-2 ring-white shadow-[0_2px_6px_-1px_rgba(0,0,0,0.15)] flex items-center justify-center`}>
              <span className="absolute inset-0 rounded-full bg-gradient-to-b from-white/35 to-transparent" />
              <span className="w-2 h-2 rounded-full bg-white/95 relative z-10" />
            </span>
            <div className="leading-tight">
              <p className={`text-sm font-bold ${TIER_STYLES[dayTier].text}`} data-testid="capacity-banner-count">
                {dayAvailableCount} <span className="font-semibold text-slate-500">/ {totalCapacity} available</span>
              </p>
              <p className="text-[11px] text-slate-500">
                {dayTier === 'full' ? 'Fully booked on this date — pick another day' :
                 dayTier === 'low' ? `Hurry — only ${dayAvailableCount} spot${dayAvailableCount === 1 ? '' : 's'} left!` :
                 dayTier === 'med' ? 'Filling up fast' :
                 'Plenty of capacity — book any slot'}
              </p>
            </div>
          </div>
          <span className={`relative z-10 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${TIER_STYLES[dayTier].text} bg-white/70 border ${TIER_STYLES[dayTier].border} shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8),0_1px_2px_0_rgba(0,0,0,0.05)]`}>
            {dayTier === 'full' ? 'Full' : dayTier === 'low' ? 'Low' : dayTier === 'med' ? 'Filling' : 'Open'}
          </span>
        </div>
      )}

      {/* Hourly Slot Grid - Two grids of 12 right after rate */}
      {isHourly && (
        <div className="px-5 pt-4 pb-2" data-testid="slot-availability-grid">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
              <span className="relative inline-flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-blue-300 to-blue-600 shadow-[0_2px_6px_-1px_rgba(59,130,246,0.55)] ring-1 ring-white/40">
                <span className="absolute inset-0 rounded-lg bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />
                <Clock size={12} className="text-white relative z-10" weight="bold" />
              </span>
              Select Hours
              {totalCapacity > 1 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-700 text-[10px] font-bold border border-indigo-200 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.7)]" data-testid="slot-total-capacity">
                  capacity {totalCapacity}
                </span>
              )}
            </Label>
            {slotsLoading && <span className="text-[10px] text-blue-400 animate-pulse">loading...</span>}
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500 flex-wrap font-medium">
              {totalCapacity > 1 ? (
                <>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-600 shadow ring-1 ring-white" /> Plenty</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 shadow ring-1 ring-white" /> Filling</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-orange-300 to-orange-600 shadow ring-1 ring-white" /> Low</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-rose-300 to-rose-600 shadow ring-1 ring-white" /> Full</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-300 to-blue-600 shadow ring-1 ring-white" /> Selected</span>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-600 shadow ring-1 ring-white" /> Available</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-rose-300 to-rose-600 shadow ring-1 ring-white" /> Booked</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-300 to-blue-600 shadow ring-1 ring-white" /> Selected</span>
                </>
              )}
            </div>
          </div>
          {/* Grid 1: 12 AM - 11 AM */}
          <div className="grid grid-cols-6 gap-1.5 mb-1.5">
            {hourlySlots.slice(0, 12).map(slot => {
              const selected = isSlotInSelection(slot.hour);
              return (
                <button
                  key={slot.hour}
                  type="button"
                  onClick={() => handleSlotClick(slot)}
                  disabled={!slot.isAvailable}
                  title={slot.closureReason || undefined}
                  className={`py-2 rounded-xl text-[11px] font-semibold border ${slotTierClass(slot, selected)}`}
                  data-testid={`slot-${slot.hour}`}
                >
                  {/* Top glassy sheen */}
                  <span className="absolute inset-x-1 top-1 h-1/2 rounded-t-lg bg-gradient-to-b from-white/45 to-transparent pointer-events-none" />
                  <div className="relative z-10 leading-tight">{slot.label}</div>
                  {slot.isClosed ? (
                    <div className="relative z-10 text-[8px] font-bold uppercase tracking-wide text-slate-400 mt-0.5" data-testid={`slot-closed-${slot.hour}`}>Closed</div>
                  ) : totalCapacity > 1 && (
                    <div className={`relative z-10 text-[9px] font-bold tracking-tight mt-0.5 ${slotSubTextClass(slot, selected)}`} data-testid={`slot-availability-${slot.hour}`}>
                      {slot.availableCount}/{totalCapacity}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {/* Grid 2: 12 PM - 11 PM */}
          <div className="grid grid-cols-6 gap-1.5">
            {hourlySlots.slice(12, 24).map(slot => {
              const selected = isSlotInSelection(slot.hour);
              return (
                <button
                  key={slot.hour}
                  type="button"
                  onClick={() => handleSlotClick(slot)}
                  disabled={!slot.isAvailable}
                  title={slot.closureReason || undefined}
                  className={`py-2 rounded-xl text-[11px] font-semibold border ${slotTierClass(slot, selected)}`}
                  data-testid={`slot-${slot.hour}`}
                >
                  <span className="absolute inset-x-1 top-1 h-1/2 rounded-t-lg bg-gradient-to-b from-white/45 to-transparent pointer-events-none" />
                  <div className="relative z-10 leading-tight">{slot.label}</div>
                  {slot.isClosed ? (
                    <div className="relative z-10 text-[8px] font-bold uppercase tracking-wide text-slate-400 mt-0.5" data-testid={`slot-closed-${slot.hour}`}>Closed</div>
                  ) : totalCapacity > 1 && (
                    <div className={`relative z-10 text-[9px] font-bold tracking-tight mt-0.5 ${slotSubTextClass(slot, selected)}`} data-testid={`slot-availability-${slot.hour}`}>
                      {slot.availableCount}/{totalCapacity}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="p-5 space-y-4">
        {/* Selected Unit Badge */}
        {selectedUnit && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between" data-testid="booking-selected-unit">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-white">{selectedUnit.label}</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-800">Slot {selectedUnit.label} selected</p>
                <p className="text-xs text-blue-600">Choose dates and time to complete booking</p>
              </div>
            </div>
            {onClearUnit && (
              <button onClick={onClearUnit} className="text-blue-400 hover:text-blue-600 p-1" data-testid="booking-clear-unit">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Selected Service Badge */}
        {selectedService && (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center justify-between" data-testid="booking-selected-service">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">{cs}</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-purple-800">{selectedService.name}</p>
                <p className="text-xs text-purple-600">{cs}{selectedService.price}{selectedService.duration_minutes ? ` / ${selectedService.duration_minutes} min` : ''}</p>
              </div>
            </div>
            {onClearService && (
              <button onClick={onClearService} className="text-purple-400 hover:text-purple-600 p-1" data-testid="booking-clear-service">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="booking-error">{error}</div>
        )}

        {hasBlockedDates && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm flex items-center gap-2" data-testid="blocked-date-warning">
            <Prohibit size={16} weight="bold" />
            Selected dates include unavailable dates. Choose different dates.
          </div>
        )}

        {fullDayClosure && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-center gap-2" data-testid="closure-notice">
            <Prohibit size={16} weight="bold" />
            <span>Space closed on <strong>{fullDayClosure.date}</strong> — {fullDayClosure.reason_label}. Please pick another date.</span>
          </div>
        )}

        {!fullDayClosure && dayClosures.length > 0 && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl text-orange-700 text-xs flex items-center gap-2" data-testid="partial-closure-notice">
            <Prohibit size={14} weight="bold" />
            <span>
              Partially closed on {startDate}: {dayClosures.map(c => {
                const label = c.duration_type === 'first_half' ? '12 AM–12 PM' : c.duration_type === 'second_half' ? '12 PM–12 AM' : `${c.start_time}–${c.end_time}`;
                return `${label} (${c.reason_label})`;
              }).join(', ')}
            </span>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">
              {config?.default_slot === 'monthly' ? 'Start Month' : 'Start Date'}
            </Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="h-11 rounded-xl text-sm" data-testid="booking-start-date"
              min={new Date().toISOString().split('T')[0]} />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">
              {config?.default_slot === 'monthly' ? 'End Month' : 'End Date'}
            </Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="h-11 rounded-xl text-sm" data-testid="booking-end-date"
              min={startDate} />
          </div>
        </div>

        {/* Time slots (for hourly/session) */}
        {(isHourly || config?.default_slot === 'per_session') && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="h-11 rounded-xl text-sm" data-testid="booking-start-time" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="h-11 rounded-xl text-sm" data-testid="booking-end-time" />
            </div>
          </div>
        )}

        {/* Guests */}
        {needsGuests && (
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Guests / Attendees</Label>
            <div className="flex items-center gap-3">
              <button onClick={() => setGuests(Math.max(1, guests - 1))}
                className="w-10 h-10 border border-slate-200 rounded-xl flex items-center justify-center text-lg font-bold text-slate-600 hover:bg-slate-50"
                data-testid="guests-minus">-</button>
              <span className="text-lg font-semibold w-8 text-center" data-testid="guests-count">{guests}</span>
              <button onClick={() => setGuests(guests + 1)}
                className="w-10 h-10 border border-slate-200 rounded-xl flex items-center justify-center text-lg font-bold text-slate-600 hover:bg-slate-50"
                data-testid="guests-plus">+</button>
            </div>
          </div>
        )}

        {/* Vehicle Type (Parking, EV) */}
        {needsVehicle && (
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Vehicle Type</Label>
            <Select value={vehicleType} onValueChange={setVehicleType}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="vehicle-type-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Car">Car</SelectItem>
                <SelectItem value="Bike">Bike</SelectItem>
                <SelectItem value="SUV">SUV</SelectItem>
                <SelectItem value="EV">Electric Vehicle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Add-Ons */}
        {addOns.length > 0 && (
          <div>
            <Label className="text-xs text-slate-500 mb-2 flex items-center gap-1">
              <Tag size={14} /> Add-Ons & Services
            </Label>
            <div className="space-y-2">
              {addOns.map(addon => {
                const isSelected = selectedAddOns.includes(addon.key);
                const priceLabel = addon.price === 0 ? 'Free'
                  : addon.price_type === 'percent' ? `+${addon.price}%`
                  : addon.price_type === 'per_guest' ? `${cs}${addon.price}/guest`
                  : addon.price_type === 'flat_per_day' ? `${cs}${addon.price}/day`
                  : `${cs}${addon.price}`;
                return (
                  <button key={addon.key} type="button" onClick={() => toggleAddOn(addon.key)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-sm transition-all ${
                      isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`addon-${addon.key}`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-emerald-500' : 'border border-slate-300'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" weight="bold" />}
                      </div>
                      <span className={isSelected ? 'text-emerald-800 font-medium' : 'text-slate-700'}>{addon.label}</span>
                    </div>
                    <span className={`text-xs font-semibold ${isSelected ? 'text-emerald-600' : 'text-slate-400'}`}>{priceLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <Label className="text-xs text-slate-500 mb-1.5 block">Special Requests (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Any specific requirements..." rows={2}
            className="rounded-xl text-sm resize-none" data-testid="booking-notes" />
        </div>
      </div>

      {/* Price Breakdown */}
      {priceInfo && (
        <div className="px-5 pb-2">
          <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>
                {priceInfo.service_label
                  ? priceInfo.service_label
                  : priceInfo.variant_label
                    ? priceInfo.variant_label
                    : 'Base'}
                {' '}
                <span className="text-slate-400">
                  ({priceInfo.service_label ? 'Service' : (SLOT_LABELS[priceInfo.pricing_model] || priceInfo.pricing_model)})
                </span>
              </span>
              <span>{cs}{priceInfo.base_cost?.toLocaleString()}</span>
            </div>
            {priceInfo.add_on_details?.map(a => (
              <div key={a.key} className="flex justify-between text-slate-500">
                <span className="flex items-center gap-1"><Check size={12} className="text-emerald-500" />{a.label}</span>
                <span>{cs}{a.cost?.toLocaleString()}</span>
              </div>
            ))}
            {priceInfo.cleaning_fee > 0 && (
              <div className="flex justify-between text-slate-500">
                <span>Cleaning Fee</span>
                <span>{cs}{priceInfo.cleaning_fee?.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-200">
              <span>Total</span>
              <span data-testid="total-price">{cs}{priceInfo.total_price?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Submit */}
      <div className="p-5 pt-3">
        {priceInfo && priceInfo.total_price === 0 && (listing.services || []).length > 0 && !selectedService && (
          <p className="text-xs text-center text-amber-600 mb-2 flex items-center justify-center gap-1" data-testid="pick-service-hint">
            <Info size={12} /> Pick a service above to see the price
          </p>
        )}
        <Button onClick={handleSubmit} disabled={submitting || !startDate || hasBlockedDates || (priceInfo?.total_price === 0 && (listing.services || []).length > 0 && !selectedService)}
          className={`w-full h-12 rounded-xl font-semibold text-white shadow-lg ${
            isApproval ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' : 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20'
          }`}
          data-testid="book-now-btn">
          {submitting ? 'Processing...' : isApproval ? `Request ${CATEGORY_LABELS[listing.category] || 'Booking'}` : `Book ${CATEGORY_LABELS[listing.category] || 'Now'}`}
        </Button>
        {isApproval && (
          <p className="text-xs text-center text-amber-600 mt-2 flex items-center justify-center gap-1">
            <Info size={12} /> Seller approval required before confirmation
          </p>
        )}
        {config?.pricing_notes && (
          <p className="text-xs text-center text-slate-400 mt-2">{config.pricing_notes}</p>
        )}
      </div>
    </div>
  );
};

export default CategoryBookingForm;
